/**
 * LayoutPreview.jsx
 *
 * Renderizza una preview scalata di un layout Fricke/Pagination
 * basata sui dati di layout_config_sample.json.
 *
 * Uso:
 *   import { LayoutPreview } from './LayoutPreview';
 *   import config from './layout_config_sample.json';
 *
 *   <LayoutPreview
 *     config={config}
 *     layoutId="S"
 *     imgSize="M"
 *     products={[csvRow]}      // array; bkg2 ne usa 3
 *     tableRows={[row1, row2]} // solo per T1/T2: righe CSV del blocco tabella
 *     debug={true}             // bordi colorati per zona (rimuovere in produzione)
 *   />
 */

import { useRef, useState, useEffect, useCallback } from 'react';

// ─── Costanti ────────────────────────────────────────────────────────────────

const PT2MM = 0.352778; // punti tipografici → mm

// Stili debug per tipo di zona (sfondo + bordo). Rimuovere debug={false} per nasconderli.
const DEBUG_STYLE = {
  text: { background: 'transparent', outline: '1px dashed rgb(189,189,189)' },
  price: { background: 'transparent', outline: '1px dashed rgb(189,189,189)' },
  image: {},
  table: { background: 'transparent', outline: '1px dashed rgb(189,189,189)' },
};

// Colore fill del placeholder immagine
const IMAGE_FILL = 'rgb(230,230,230)'; // leggermente più scuro del cell per distinguere l'area immagine

// Default inset testo (mm) — usati come fallback se config.meta.textInsets non è presente
const DEFAULT_TEXT_INSETS = { top: 2.5, left: 2.5, right: 2.5, bottom: 0, bottomMulticol: 2.5 };

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Converte mm in px usando il fattore di scala corrente. */
const mm = (val, scale) => val * scale;

/** Stile base per una zona (position absolute, coordinate in mm → px). */
function zoneStyle(zone, scale, type, debug) {
  return {
    position: 'absolute',
    left: mm(zone.x, scale),
    top: mm(zone.y, scale),
    width: mm(zone.w, scale),
    height: mm(zone.h, scale),
    overflow: 'hidden',
    boxSizing: 'border-box',
    ...(debug ? DEBUG_STYLE[type] ?? {} : {}),
  };
}

/**
 * Calcola il font size in px da punti tipografici InDesign.
 * fontSize (pt) × PT2MM (mm/pt) × scale (px/mm)
 */
const ptToPx = (pt, scale) => pt * PT2MM * scale;

/** Recupera il valore di un campo prodotto tramite csvMapping o fallback diretto. */
function getField(product, fieldName, csvMapping) {
  if (!product) return '';
  // Cerca prima la chiave diretta, poi tramite csvMapping
  if (product[fieldName] !== undefined) return product[fieldName] ?? '';
  const mapped = csvMapping?.[fieldName];
  if (mapped && product[mapped] !== undefined) return product[mapped] ?? '';
  // Fallback case-insensitive
  const lower = fieldName.toLowerCase();
  const key = Object.keys(product).find(k => k.toLowerCase() === lower);
  return key ? (product[key] ?? '') : '';
}

/** Renderizza un valore stringa.
 *  I tag <br> vengono convertiti in line break (<br/>).
 *  Nota: nell'IDML i <br> sono rimossi da EasyCatalog (testo continuo),
 *  ma nella preview li manteniamo per approssimare meglio l'altezza verticale
 *  occupata dal testo in InDesign (ogni bullet point su riga propria). */
function renderValue(value) {
  const parts = String(value).split(/<br\s*\/?>/gi);
  if (parts.length === 1) return parts[0];
  return parts.map((part, i) =>
    i < parts.length - 1
      ? <span key={i}>{part}<br /></span>
      : <span key={i}>{part}</span>
  );
}

// ─── Zone renderers ───────────────────────────────────────────────────────────

/** Zona testo: campi EC in sequenza con stili tipografici.
 *  I campi con fieldStyles.inline=true vengono raggruppati sulla stessa riga.
 *  Se showPrice=true, i campi prezzo vengono renderizzati inline in fondo al flusso
 *  (come in InDesign, dove il prezzo è un oggetto ancorato dentro il TextFrame desc). */
function TextZone({ zone, scale, product, fieldStyles, csvMapping, debug, onOverflow, onTextMeasure, showPrice, priceSlotHeight, metaConfig }) {
  // Raggruppa i campi: consecutivi con inline:true vanno nella stessa riga
  const groups = [];
  for (const fieldName of zone.fields ?? []) {
    const isInline = !!(fieldStyles?.[fieldName]?.inline);
    const last = groups[groups.length - 1];
    if (isInline && last?.inline) {
      last.fields.push(fieldName);
    } else {
      groups.push({ inline: isInline, fields: [fieldName] });
    }
  }

  const isMultiCol = zone.columns > 1;
  const ti = metaConfig?.textInsets ?? DEFAULT_TEXT_INSETS;
  const bottomInset = isMultiCol ? (ti.bottomMulticol ?? ti.bottom ?? 0) : (ti.bottom ?? 0);

  const textRef = useRef(null)

  // Forza column-fill:auto direttamente sul DOM — inline styles React possono non funzionare
  // per questa proprietà in alcuni browser (Chrome bug noto).
  useEffect(() => {
    const el = textRef.current
    if (!el || !isMultiCol) return
    el.style.setProperty('column-fill', 'auto', 'important')
    el.style.setProperty('-webkit-column-fill', 'auto', 'important')
    el.style.setProperty('-moz-column-fill', 'auto', 'important')
  }, [isMultiCol])

  // Contenuto testuale per ri-eseguire overflow detection quando il testo cambia
  const textContent = (zone.fields ?? []).map(f => getField(product, f, csvMapping) || '').join('|')

  useEffect(() => {
    const el = textRef.current
    if (!el || el.clientHeight === 0) return
    let cancelled = false
    document.fonts.ready.then(() => {
      if (cancelled || !el) return
      const actualHeight = el.getBoundingClientRect().height

      if (isMultiCol) {
        // con column-fill:auto il testo in eccesso crea colonne extra oltre la larghezza del contenitore
        const savedOv = el.style.overflow
        el.style.overflow = 'visible'
        const hasOverflow = el.scrollWidth > Math.ceil(el.clientWidth) + 1
        el.style.overflow = savedOv
        if (hasOverflow) onOverflow?.()
        if (onTextMeasure) {
          const savedH = el.style.height
          const savedOv2 = el.style.overflow
          el.style.height = 'auto'
          el.style.overflow = 'visible'
          const naturalH = el.scrollHeight
          el.style.height = savedH
          el.style.overflow = savedOv2
          onTextMeasure(naturalH)
        }
      } else {
        const savedH = el.style.height
        const savedOv = el.style.overflow
        el.style.height = 'auto'
        el.style.overflow = 'visible'
        const naturalH = el.scrollHeight
        el.style.height = savedH
        el.style.overflow = savedOv
        if (naturalH > Math.ceil(actualHeight) + 1) onOverflow?.()
        if (onTextMeasure) onTextMeasure(naturalH)
      }
    })
    return () => { cancelled = true }
  }, [zone.h, scale, onOverflow, onTextMeasure, textContent]) // eslint-disable-line react-hooks/exhaustive-deps

  const fontFam = metaConfig?.fonts
    ? `'${metaConfig.fonts.fontFamily}', ${metaConfig.fonts.fallback}`
    : "'Frutiger Next LT W1G', Arial Narrow, sans-serif";

  const fieldStyle = (fieldName) => {
    const fs = fieldStyles?.[fieldName] ?? {};
    const isBold = /bold|heavy/i.test(fs.fontStyle ?? '');
    const isCondensed = /condensed/i.test(fs.fontStyle ?? '');
    const fontSizePx = ptToPx(fs.fontSize ?? 8, scale);
    return {
      fontFamily: fontFam,
      fontSize: fontSizePx,
      lineHeight: `${ptToPx(fs.leading ?? fontSizePx / PT2MM * 1.2, scale)}px`,
      fontWeight: isBold ? 700 : 300,
      fontStretch: isCondensed ? 'condensed' : 'normal',
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      hyphens: 'none',
      color: '#111',
    };
  };

  // Prezzo inline: raccogli i valori prezzo dal prodotto usando meta.priceFields
  const priceFields = showPrice ? (() => {
    const pf = metaConfig?.priceFields ?? [
      { field: 'Price crossed', crossed: true },
      { field: 'Price + Currency', crossed: false },
      { field: 'Price crossed 2', crossed: true },
      { field: 'Price 2 + Currency', crossed: false },
    ];
    const items = [];
    for (const pdef of pf) {
      const val = getField(product, pdef.field, csvMapping);
      if (val) items.push({ value: val, crossed: !!pdef.crossed });
    }
    const gnField = metaConfig?.grossNetField ?? 'Gross or Net';
    const grossNet = getField(product, gnField, csvMapping);
    return { items, grossNet };
  })() : null;

  const ps = metaConfig?.priceStyle ?? { fontSize: 7.5, leading: 9 };
  const priceFontSizePx = ptToPx(ps.fontSize, scale);
  const priceLineHeight = `${ptToPx(ps.leading, scale)}px`;

  const outerStyle = {
    ...zoneStyle(zone, scale, 'text', debug),
    padding: `${mm(ti.top ?? 2.5, scale)}px ${mm(ti.right ?? 2.5, scale)}px ${mm(bottomInset, scale)}px ${mm(ti.left ?? 2.5, scale)}px`,
    ...(isMultiCol ? {
      display: 'block',
      columnCount: zone.columns,
      columnGap: mm(zone.columnGap ?? 0, scale),
    } : {
      display: 'flex',
      flexDirection: 'column',
    }),
  };

  // Contenuto condiviso: campi testo + prezzo inline
  const content = (<>
    {groups.map((group, gi) => {
      const firstFs = fieldStyles?.[group.fields[0]] ?? {};
      const groupMargin = {
        marginTop: firstFs.spaceBefore ? mm(firstFs.spaceBefore, scale) : 0,
        marginBottom: firstFs.spaceAfter ? mm(firstFs.spaceAfter, scale) : 0,
      };

      if (!group.inline) {
        const fieldName = group.fields[0];
        const value = getField(product, fieldName, csvMapping) || (debug ? fieldName : '');
        const fs = fieldStyles?.[fieldName] ?? {};
        // IDML: SpanColumnType="SpanColumns" — il titolo attraversa entrambe le colonne
        const spanStyle = (isMultiCol && fs.spanColumns) ? { columnSpan: 'all' } : {};
        return (
          <div key={gi} style={{ ...fieldStyle(fieldName), ...groupMargin, ...spanStyle }}>
            {renderValue(value)}
          </div>
        );
      }

      // Campi inline: span consecutivi dentro un div.
      // Il div DEVE avere lineHeight esplicito, altrimenti il browser usa il default (~19px)
      // che è più alto del leading InDesign, creando un line box troppo alto.
      return (
        <div key={gi} style={{ ...fieldStyle(group.fields[0]), ...groupMargin, breakInside: 'avoid' }}>
          {group.fields.map(fieldName => {
            const value = getField(product, fieldName, csvMapping) || (debug ? fieldName : '');
            return (
              <span key={fieldName} style={fieldStyle(fieldName)}>
                {renderValue(value)}{' '}
              </span>
            );
          })}
        </div>
      );
    })}
    {/* Prezzo inline — fluisce nel testo come in InDesign (anchored object in desc),
          racchiuso in un box a altezza fissa come la vecchia PriceZone */}
    {showPrice && (
      <div style={{
        height: mm(priceSlotHeight ?? 11.026, scale),
        marginTop: mm(1, scale),  // IDML: Price paragraph spaceBefore = 1mm
        flexShrink: 0,             // altezza fissa (solo in flex; in multi-col non ha effetto)
        breakInside: 'avoid',       // impedisce spezzatura tra colonne
        display: 'flex',
        alignItems: 'center',
        fontFamily: fontFam,
        fontSize: priceFontSizePx,
        lineHeight: priceLineHeight,
        fontWeight: 700,
        fontStretch: 'condensed',
        whiteSpace: 'nowrap',
        gap: mm(1, scale),
        boxSizing: 'border-box',
        ...(debug ? { outline: '1px dashed rgb(189,189,189)' } : {}),
      }}>
        {priceFields && priceFields.items.length > 0 ? (<>
          {priceFields.grossNet && (
            <span style={{ fontSize: priceFontSizePx, fontWeight: 300, color: '#555' }}>
              {priceFields.grossNet}
            </span>
          )}
          {priceFields.items.map((item, pi) => (
            <span key={pi} style={{
              color: item.crossed ? '#9ca3af' : '#dc2626',
              textDecoration: item.crossed ? 'line-through' : 'none',
            }}>
              {item.value}
            </span>
          ))}
        </>) : debug && (
          <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>prezzo</span>
        )}
      </div>
    )}
  </>);

  return (
    <div ref={textRef} style={outerStyle}>
      {content}
    </div>
  );
}

/** Zona prezzo: campi prezzo letti da config.meta.priceFields, eventualmente barrati. */
function PriceZone({ zone, scale, product, csvMapping, debug, metaConfig }) {
  const fontFam = metaConfig?.fonts
    ? `'${metaConfig.fonts.fontFamily}', ${metaConfig.fonts.fallback}`
    : "'Frutiger Next LT W1G', Arial Narrow, sans-serif";
  const ti = metaConfig?.textInsets ?? DEFAULT_TEXT_INSETS;

  const pf = metaConfig?.priceFields ?? [
    { field: 'Price crossed', crossed: true },
    { field: 'Price + Currency', crossed: false },
    { field: 'Price crossed 2', crossed: true },
    { field: 'Price 2 + Currency', crossed: false },
  ];
  const items = [];
  for (const pdef of pf) {
    const val = getField(product, pdef.field, csvMapping);
    if (val) items.push({ value: val, crossed: !!pdef.crossed });
  }

  const fontSizePx = ptToPx(10, scale);

  const priceItemStyle = (crossed) => ({
    fontFamily: fontFam,
    fontSize: fontSizePx,
    lineHeight: `${fontSizePx * 1.2}px`,
    fontWeight: 700,
    color: crossed ? '#9ca3af' : '#dc2626',
    textDecoration: crossed ? 'line-through' : 'none',
    marginRight: mm(1, scale),
    whiteSpace: 'nowrap',
  });

  return (
    <div style={{ ...zoneStyle(zone, scale, 'price', debug), display: 'flex', alignItems: 'center', paddingLeft: mm(ti.left ?? 2.5, scale) }}>
      {items.map((item, i) => (
        <span key={i} style={priceItemStyle(item.crossed)}>
          {item.value}
        </span>
      ))}
      {items.length === 0 && debug && (
        <span style={{ fontSize: fontSizePx, color: '#9ca3af', fontStyle: 'italic' }}>
          prezzo
        </span>
      )}
    </div>
  );
}

/** Zona immagine: mostra l'immagine reale (da URL pattern nel config) oppure il placeholder grigio,
 *  in base alla prop `showImages`. Il toggle è controllato dalla GridView. */
function ImageZone({ zone, scale, debug, product, csvMapping, showImages, metaConfig }) {
  const fontSizePx = Math.max(ptToPx(6, scale), 7);
  let imgUrl = null;
  if (showImages && product && metaConfig?.imageUrlPattern) {
    const imageField = metaConfig.imageField ?? metaConfig.csvMapping?.['articleNumber'] ?? 'Article Number';
    const fallbackField = metaConfig.imageFieldFallback ?? metaConfig.csvMapping?.['image'] ?? 'Picture Name';
    const artNum = getField(product, imageField, csvMapping)
      || getField(product, fallbackField, csvMapping)?.replace(/\.[^.]+$/, '');
    if (artNum) {
      imgUrl = metaConfig.imageUrlPattern.replace('{articleNumber}', artNum);
    }
  }
  const [imgError, setImgError] = useState(false);
  useEffect(() => { setImgError(false); }, [imgUrl]);



  return (
    <div
      style={{
        ...zoneStyle(zone, scale, 'image', debug),
        background: IMAGE_FILL,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {imgUrl && !imgError ? (
        <img
          src={imgUrl}
          alt=""
          loading="lazy"
          onError={() => setImgError(true)}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            objectPosition: 'center',
          }}
        />
      ) : debug && (
        <span style={{ fontSize: fontSizePx, color: '#888888', fontFamily: 'monospace', userSelect: 'none' }}>
          IMG
        </span>
      )}
    </div>
  );
}

/**
 * Zona tabella EasyCatalog (T1/T2).
 * - T1: tabella singola, larghezza intera zona
 * - T2: due tabelle affiancate (zone.columns === 2), righe splittate a metà
 *
 * In debug mode, se tableRows è vuoto mostra righe placeholder con i nomi dei campi.
 */
/**
 * TableContent — renderizza il markup <table> puro (header + righe dati).
 * Componente riusato sia da TableZone (rendering in-place) sia da TableMeasurer
 * (misurazione hidden per il reflow). Zero side effect: solo markup.
 *
 * Ogni <tr> del tbody riceve un attributo `data-row-index` per la misurazione.
 */
export function TableContent({ tableColumns, tableRows, csvMapping, debug, metaConfig, scale, tableRef }) {
  const cols = tableColumns ?? [];
  const rows = tableRows ?? [];

  const fontFam = metaConfig?.fonts
    ? `'${metaConfig.fonts.fontFamily}', ${metaConfig.fonts.fallback}`
    : "'Frutiger Next LT W1G', Arial Narrow, sans-serif";
  const ths = metaConfig?.tableHeaderStyle ?? { fontSize: 8.5, fontWeight: 700, fontStretch: 'condensed', scaleX: 0.95, textAlign: 'center' };
  const tcs = metaConfig?.tableCellStyle ?? { fontSize: 8, leading: 10, fontWeight: 300, fontStretch: 'condensed' };

  // InDesign: TextTopInset = TextLeftInset = 4pt
  const cellPad = Math.max(ptToPx(4, scale), 2);
  const fontSz = Math.max(ptToPx(tcs.fontSize, scale), 6);
  const hdrFontSz = Math.max(ptToPx(ths.fontSize, scale), 5.5);

  const baseTableStyle = {
    borderCollapse: 'collapse',
    width: '100%',
    tableLayout: 'fixed',
  };

  const thStyle = (col) => ({
    width: col.wMm * scale,
    fontSize: hdrFontSz,
    fontFamily: fontFam,
    fontStretch: ths.fontStretch ?? 'condensed',
    fontWeight: ths.fontWeight ?? 700,
    textAlign: ths.textAlign ?? 'center',
    transform: ths.scaleX ? `scaleX(${ths.scaleX})` : 'none',
    padding: `${cellPad * 0.5}px ${cellPad}px`,
    background: 'rgb(210,210,210)',
    border: 'none',
    borderRight: '1px solid rgb(189,189,189)',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  });

  const tdStyle = (ri) => ({
    fontSize: fontSz,
    lineHeight: `${Math.max(ptToPx(tcs.leading ?? 10, scale), 8)}px`,
    fontFamily: fontFam,
    fontStretch: tcs.fontStretch ?? 'condensed',
    fontWeight: tcs.fontWeight ?? 300,
    textAlign: 'left',
    verticalAlign: 'top',
    hyphens: 'none',
    padding: `${cellPad * 0.5}px ${cellPad}px`,
    background: ri % 2 === 0 ? 'rgb(240,240,240)' : 'rgb(248,248,248)',
    border: '1px solid rgb(189,189,189)',
    color: '#111',
  });

  // Righe da mostrare: dati reali, oppure placeholder in debug
  const displayRows = rows.length > 0
    ? rows
    : (debug ? [{}, {}, {}] : []); // 3 righe vuote come placeholder

  return (
    <table ref={tableRef} style={baseTableStyle}>
      <thead>
        <tr style={{ background: 'rgb(210,210,210)' }}>
          {cols.map((col, ci) => (
            <th key={ci} style={thStyle(col)}>
              {col.label || ''}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {displayRows.map((row, ri) => (
          <tr key={ri} data-row-index={ri}>
            {cols.map((col, ci) => {
              const raw = col.fields ?? col.field;
              const fieldList = Array.isArray(raw)
                ? raw.map(f => typeof f === 'string' ? { field: f } : f)
                : raw ? [{ field: raw }] : [];
              return (
                <td key={ci} style={tdStyle(ri)}>
                  {fieldList.map((fieldDef) => {
                    const f = fieldDef.field;
                    const val = getField(row, f, csvMapping);
                    if (!val) return null;
                    return (
                      <div key={f} style={{ fontWeight: fieldDef.bold ? 700 : tcs.fontWeight ?? 300 }}>
                        {renderValue(val)}
                      </div>
                    );
                  })}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─── TableMeasurer ────────────────────────────────────────────────────────────

/**
 * Scala di misurazione (px/mm). Deve essere abbastanza alta da rendere i font
 * accuratamente (scale=1 → font 2-3px, rendering inaffidabile).
 * A scale=4: font 8pt → 11.3px, leading 10pt → 14.1px (dimensioni normali).
 */
const MEASURE_SCALE = 4;

/**
 * TableMeasurer — renderizza TableContent in un container hidden a scala fissa,
 * misura offsetHeight di ogni <tr> del tbody e dell'header, invoca `onMeasured`
 * con i valori in mm. Non produce output visibile.
 *
 * Usato dall'orchestrazione in GridView.js per calcolare split point dei blocchi
 * T/T2 che eccedono la capacità del tier massimo (reflow su più pagine).
 *
 * onMeasured({ rowHeights: number[] in mm, headerHeight: number in mm }).
 */
export function TableMeasurer({ config, layoutId, imgSize, tableRows, onMeasured }) {
  const tableRef = useRef(null);
  const didMeasureRef = useRef(false);

  const layout    = config?.layouts?.find(l => l.id === layoutId);
  const variant   = layout?.variants?.find(v => v.imgSize === imgSize) ?? layout?.variants?.[0];
  const tableZone = variant?.zones?.find(z => z.type === 'table');
  const columns   = tableZone?.columns ?? 1;
  const columnWmm = tableZone ? tableZone.w / columns : 0;

  const tableColumns = layout?.tableColumns ?? [];
  const csvMapping   = config?.meta?.csvMapping ?? {};
  const metaConfig   = config?.meta;

  useEffect(() => {
    if (didMeasureRef.current) return;
    if (!tableRef.current || columnWmm <= 0) return;
    let cancelled = false;
    document.fonts.ready.then(() => {
      if (cancelled || !tableRef.current) return;
      const trs = Array.from(tableRef.current.querySelectorAll('tbody tr'));
      const headerTr = tableRef.current.querySelector('thead tr');
      const rowHeights = trs.map(tr => tr.getBoundingClientRect().height / MEASURE_SCALE);
      const headerHeight = (headerTr?.getBoundingClientRect().height ?? 0) / MEASURE_SCALE;
      didMeasureRef.current = true;
      onMeasured?.({ rowHeights, headerHeight });
    });
    return () => { cancelled = true; };
  }, [columnWmm, tableRows.length, onMeasured]);

  if (columnWmm <= 0 || tableColumns.length === 0) return null;

  return (
    <div style={{
      position: 'absolute',
      visibility: 'hidden',
      pointerEvents: 'none',
      top: -10000,
      left: 0,
      width: columnWmm * MEASURE_SCALE,
    }} aria-hidden="true">
      <TableContent
        tableRef={tableRef}
        tableColumns={tableColumns}
        tableRows={tableRows}
        csvMapping={csvMapping}
        debug={false}
        metaConfig={metaConfig}
        scale={MEASURE_SCALE}
      />
    </div>
  );
}

function TableZone({ zone, scale, tableColumns, tableRows, rowHeights, rowsHeaderHeight, cellHeightMm, csvMapping, debug, onTableMeasure, metaConfig, chunkIndex = 0, totalChunks = 1 }) {
  const rows = tableRows ?? [];

  // Misura l'altezza reale del contenuto tabella dopo render + font loading.
  // measureRef punta a un wrapper interno SENZA altezza esplicita: il suo offsetHeight
  // riflette l'altezza naturale del contenuto (non la zone.h del config che è sempre 209mm).
  const measureRef = useRef(null);
  useEffect(() => {
    const el = measureRef.current;
    if (!el || scale <= 0) return;
    let cancelled = false;
    document.fonts.ready.then(() => {
      if (cancelled || !el) return;
      const contentHeightMm = el.offsetHeight / scale;
      onTableMeasure?.(zone.y + contentHeightMm);
    });
    return () => { cancelled = true; };
  }, [tableRows, scale, zone.y, onTableMeasure]);

  // Righe da mostrare: dati reali, oppure placeholder in debug (per T2 split)
  const displayRows = rows.length > 0
    ? rows
    : (debug ? [{}, {}, {}] : []);

  // T2: due colonne InDesign affiancate — fill sequenziale (col1 fino a capacità, poi col2).
  // Se rowHeights è disponibile (blocco misurato), usa le altezze reali per il punto di split.
  // Altrimenti fallback a ceil(N/2).
  if (zone.columns === 2) {
    let splitIdx = Math.ceil(displayRows.length / 2); // fallback bilanciato
    if (rowHeights?.length && displayRows.length > 0) {
      // Usa l'altezza effettiva disponibile: min(zone.h, cellHeight - zone.y).
      // Allineato con fitAdaptiveChunk che usa la stessa formula.
      const effectiveZoneH = cellHeightMm
        ? Math.min(zone.h, cellHeightMm - zone.y)
        : zone.h;
      const availMm = effectiveZoneH - (rowsHeaderHeight ?? 0);
      let cum = 0;
      splitIdx = 0;
      for (let i = 0; i < Math.min(rowHeights.length, displayRows.length); i++) {
        if (cum + rowHeights[i] > availMm) break;
        cum += rowHeights[i];
        splitIdx++;
      }
      if (splitIdx === 0) splitIdx = 1; // almeno 1 riga a sinistra
    }
    const leftRows = displayRows.slice(0, splitIdx);
    const rightRows = displayRows.slice(splitIdx).length > 0
      ? displayRows.slice(splitIdx)
      : (debug ? displayRows.slice(0, splitIdx) : []);

    return (
      <div style={{
        ...zoneStyle(zone, scale, 'table', debug),
        display: 'flex',
        alignItems: 'flex-start',
      }}>
        <div ref={measureRef} style={{ display: 'flex', width: '100%', alignItems: 'flex-start' }}>
          <div style={{ width: '50%', overflow: 'hidden' }}>
            <TableContent tableColumns={tableColumns} tableRows={leftRows} csvMapping={csvMapping} debug={debug} metaConfig={metaConfig} scale={scale} />
          </div>
          <div style={{ width: '50%', overflow: 'hidden' }}>
            <TableContent tableColumns={tableColumns} tableRows={rightRows} csvMapping={csvMapping} debug={debug} metaConfig={metaConfig} scale={scale} />
          </div>
        </div>
      </div>
    );
  }

  // T1: tabella singola, larghezza intera zona
  return (
    <div style={zoneStyle(zone, scale, 'table', debug)}>
      <div ref={measureRef}>
        <TableContent tableColumns={tableColumns} tableRows={displayRows} csvMapping={csvMapping} debug={debug} metaConfig={metaConfig} scale={scale} />
      </div>
    </div>
  );
}

// ─── Renderer cella ──────────────────────────────────────────────────────────

/**
 * Renderizza le zone di una singola cella (già in coordinate assolute mm).
 * product: oggetto CSV per questo slot.
 */
/** Restituisce true se il prodotto ha almeno un campo prezzo valorizzato.
 *  Legge i nomi dei campi da meta.priceFields (con fallback hardcoded). */
function hasAnyPrice(product, csvMapping, metaConfig) {
  const pf = metaConfig?.priceFields ?? [
    { field: 'Price + Currency' }, { field: 'Price 2 + Currency' },
    { field: 'Price crossed' }, { field: 'Price crossed 2' },
  ];
  return pf.some(p => !!getField(product, p.field, csvMapping));
}

function CellZones({ zones, scale, product, fieldStyles, csvMapping, tiers, tableColumns, tableRows, rowHeights, rowsHeaderHeight, cellHeightMm, debug, onOverflow, onTextMeasure, onTableMeasure, priceSlotHeight, showImages, metaConfig, chunkIndex = 0, totalChunks = 1 }) {
  // Fonde la zona price nella zona text: il prezzo fluisce inline come in InDesign
  const priceZone = zones.find(z => z.type === 'price');
  const activeZones = priceZone
    ? zones
      .filter(z => z.type !== 'price')
      .map(z => z.type === 'text' ? { ...z, h: z.h + priceZone.h } : z)
    : zones;

  return activeZones.map((zone, i) => {
    const key = `${zone.type}-${i}`;
    switch (zone.type) {
      case 'text':
        return (
          <TextZone
            key={key} zone={zone} scale={scale}
            product={product} fieldStyles={fieldStyles} csvMapping={csvMapping}
            debug={debug}
            onOverflow={onOverflow}
            onTextMeasure={onTextMeasure}
            showPrice={!!priceZone && hasAnyPrice(product, csvMapping, metaConfig)}
            priceSlotHeight={priceSlotHeight}
            metaConfig={metaConfig}
          />
        );
      case 'image':
        return <ImageZone key={key} zone={zone} scale={scale} debug={debug} product={product} csvMapping={csvMapping} showImages={showImages} metaConfig={metaConfig} />;
      case 'table':
        return (
          <TableZone
            key={key} zone={zone} scale={scale}
            tableColumns={tableColumns} tableRows={tableRows}
            rowHeights={rowHeights} rowsHeaderHeight={rowsHeaderHeight}
            cellHeightMm={cellHeightMm}
            csvMapping={csvMapping}
            debug={debug}
            onTableMeasure={onTableMeasure}
            metaConfig={metaConfig}
            chunkIndex={chunkIndex}
            totalChunks={totalChunks}
          />
        );
      default:
        return null;
    }
  });
}

// ─── Componente principale ────────────────────────────────────────────────────

/**
 * LayoutPreview
 *
 * @param {object}          config     - JSON completo (layout_config_sample.json)
 * @param {string}          layoutId   - es. "S", "bkg2", "T1"
 * @param {string}          imgSize    - "S" | "M" | "L"
 * @param {object|object[]} products   - riga CSV singola o array (bkg2 ne usa fino a 3)
 * @param {object[]}        tableRows    - solo T1/T2: tutte le righe CSV del blocco tabella
 * @param {number}          cellOverride - altezza cella in mm (sovrascrive cell.h dell'IDMS)
 *                                         usato da GridItem per T1/T2 con altezza tier reale
 * @param {boolean}         debug        - mostra bordi colorati per zona (default: true)
 */
export function LayoutPreview({ config, layoutId, imgSize, products, tableRows = [], rowHeights, rowsHeaderHeight, chunkIndex = 0, totalChunks = 1, cellOverride, precomputedWidth, debug = true, onOverflow, onTextMeasure, onTableMeasure, showImages = false, renderSlotOverlay }) {
  const containerRef = useRef(null);
  const slotOverflowCbs = useRef([]);
  const slotTextMeasureCbs = useRef([]);
  const [measuredWidth, setMeasuredWidth] = useState(0);
  const containerWidth = precomputedWidth > 0 ? precomputedWidth : measuredWidth;

  const metaConfig = config?.meta;

  // Carica i font da config.meta.fonts — genera @font-face dinamicamente
  useEffect(() => {
    const fonts = config?.meta?.fonts;
    if (!fonts?.fontFaces?.length) return;
    const base = process.env.PUBLIC_URL ?? '';
    const style = document.createElement('style');
    style.dataset.layoutPreviewFonts = '1';
    style.textContent = fonts.fontFaces.map(ff => `
      @font-face {
        font-family: '${fonts.fontFamily}';
        src: url('${base}${ff.src}') format('${ff.format || 'opentype'}');
        font-weight: ${ff.weight ?? 400};
        font-stretch: ${ff.stretch ?? 'normal'};
      }
    `).join('\n');
    // Evita duplicati se il componente viene montato più volte
    // Non rimuovere al cleanup: i font sono globali e servono anche quando il componente
    // viene smontato/rimontato al cambio pagina (rimuoverli causa flash con font fallback)
    if (!document.querySelector('[data-layout-preview-fonts]')) {
      document.head.appendChild(style);
    }
  }, [config?.meta?.fonts]);

  // ResizeObserver fallback: usato solo se precomputedWidth non è fornito
  useEffect(() => {
    if (precomputedWidth > 0) return;   // larghezza già nota dal parent
    const el = containerRef.current;
    if (!el) return;
    setMeasuredWidth(el.getBoundingClientRect().width);
    const ro = new ResizeObserver(entries => {
      setMeasuredWidth(entries[0].contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [precomputedWidth]);

  // Normalizza products in array
  const productArray = Array.isArray(products) ? products : products ? [products] : [];

  // Ricava layout e variante dal config
  const layout = config?.layouts?.find(l => l.id === layoutId);
  const variant = layout?.variants?.find(v => v.imgSize === imgSize);

  if (!layout || !variant) {
    return (
      <div ref={containerRef} style={{ padding: 8, color: '#dc2626', fontSize: 12 }}>
        Layout non trovato: {layoutId} / {imgSize}
      </div>
    );
  }

  const cell = variant.cell ?? layout.cell;
  const fieldStyles = config?.meta?.fieldStyles ?? {};
  const csvMapping = config?.meta?.csvMapping ?? {};
  const tableColumns = layout.tableColumns ?? [];
  const scale = containerWidth > 0 ? containerWidth / cell.w : 0;
  // cellOverride: altezza reale in mm (usato da GridItem per T1/T2 per correggere
  // il mismatch tra cell.h dell'IDMS = 250mm e l'altezza effettiva del tier)
  const effectiveH = cellOverride ?? cell.h;
  const cellHeightPx = effectiveH * scale;

  // ── Calcolo zone assolute ──────────────────────────────────────────────────
  // Tre casi:
  //   1. variant.slots   → bkg2: N slot ciascuno con offset proprio
  //   2. layout.slotOffset → bkg1: zone traslate di slotOffset
  //   3. altrimenti      → zone dirette (standard, T1/T2)

  let slots; // array di { zones: ZoneObj[], product: object }

  if (variant.slots) {
    // bkg2: up to 3 prodotti, ciascuno con il proprio imgSize
    slots = variant.slots.map((slot, i) => {
      const prod = productArray[i] ?? productArray[0] ?? null;
      const fImgSize = metaConfig?.csvMapping?.['imgSize'] ?? 'Image Size';
      const slotImgSize = prod?.[fImgSize] || imgSize;
      const slotVariant = layout.variants?.find(v => v.imgSize === slotImgSize) ?? variant;
      const slotZones = slotVariant.slots?.[i]?.zones ?? slot.zones;
      const slotOffset = slotVariant.slots?.[i]?.offset ?? slot.offset;
      const absZones = slotZones.map(z => ({ ...z, x: z.x + slotOffset.x, y: z.y + slotOffset.y }));
      const bx = Math.min(...absZones.map(z => z.x));
      const by = Math.min(...absZones.map(z => z.y));
      const bx2 = Math.max(...absZones.map(z => z.x + z.w));
      const by2 = Math.max(...absZones.map(z => z.y + z.h));
      return {
        zones: absZones.map(z => ({ ...z, x: z.x - bx, y: z.y - by })),
        product: prod,
        bounds: { x: bx, y: by, w: bx2 - bx, h: by2 - by },
      };
    });
  } else if (layout.slotOffset) {
    // bkg1: slot singolo traslato
    const absZones = (variant.zones ?? []).map(z => ({
      ...z,
      x: z.x + layout.slotOffset.x,
      y: z.y + layout.slotOffset.y,
    }));
    const bx = Math.min(...absZones.map(z => z.x));
    const by = Math.min(...absZones.map(z => z.y));
    const bx2 = Math.max(...absZones.map(z => z.x + z.w));
    const by2 = Math.max(...absZones.map(z => z.y + z.h));
    slots = [{
      zones: absZones.map(z => ({ ...z, x: z.x - bx, y: z.y - by })),
      product: productArray[0] ?? null,
      bounds: { x: bx, y: by, w: bx2 - bx, h: by2 - by },
    }];
  } else {
    // Standard / T1 / T2 — nessun wrapper, coordinate invariate
    let zonesToRender = variant.zones ?? [];

    // Chunk di continuazione (chunkIndex > 0): la cella ha solo la tabella,
    // nessuna immagine. Filtra image zone ed estende la table zone a pagina intera.
    if (chunkIndex > 0) {
      const imageZone = zonesToRender.find(z => z.type === 'image');
      // Usa lo stesso top margin dell'image (~5mm), simmetrico bottom
      const topMarginMm    = imageZone?.y ?? 5;
      const bottomMarginMm = topMarginMm;
      zonesToRender = zonesToRender
        .filter(z => z.type !== 'image')
        .map(z => z.type === 'table'
          ? { ...z, y: topMarginMm, h: effectiveH - topMarginMm - bottomMarginMm }
          : z
        );
    }

    slots = [{
      zones: zonesToRender,
      product: productArray[0] ?? null,
      bounds: null,
    }];
  }

  // Callback stabili per-slot: evita ri-creazione ad ogni render (causa loop useEffect in TextZone)
  if (slots.length > 1) {
    if (slotOverflowCbs.current._of !== onOverflow || slotOverflowCbs.current.length !== slots.length) {
      slotOverflowCbs.current = slots.map((_, si) => () => onOverflow?.(si));
      slotOverflowCbs.current._of = onOverflow;
    }
    if (slotTextMeasureCbs.current._tm !== onTextMeasure || slotTextMeasureCbs.current.length !== slots.length) {
      slotTextMeasureCbs.current = slots.map((_, si) => (sh) => onTextMeasure?.(sh, si));
      slotTextMeasureCbs.current._tm = onTextMeasure;
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      {scale > 0 && (
        <div
          style={{
            position: 'relative',
            width: containerWidth,
            height: cellHeightPx,
            overflow: 'hidden',
            background: 'rgb(245,245,245)',
            border: '1px solid rgb(189,189,189)',
            flexShrink: 0,
            ...(debug ? { outline: '1px solid #d1d5db' } : {}),
          }}
        >
          {slots.map((slot, si) => {
            const cellZones = (
              <CellZones
                key={slot.bounds ? undefined : si}
                zones={slot.zones}
                scale={scale}
                product={slot.product}
                fieldStyles={fieldStyles}
                csvMapping={csvMapping}
                tiers={variant.tiers}
                tableColumns={tableColumns}
                tableRows={tableRows}
                rowHeights={rowHeights}
                rowsHeaderHeight={rowsHeaderHeight}
                cellHeightMm={effectiveH}
                chunkIndex={chunkIndex}
                totalChunks={totalChunks}
                debug={debug}
                onOverflow={slots.length > 1 ? slotOverflowCbs.current[si] : onOverflow}
                onTextMeasure={slots.length > 1 ? slotTextMeasureCbs.current[si] : onTextMeasure}
                onTableMeasure={onTableMeasure}
                priceSlotHeight={config?.meta?.priceSlotHeight ?? 11.026}
                showImages={showImages}
                metaConfig={metaConfig}
              />
            );
            if (slot.bounds) {
              const b = slot.bounds;
              return (
                <div key={si} style={{
                  position: 'absolute',
                  left: mm(b.x, scale),
                  top: mm(b.y, scale),
                  width: mm(b.w, scale),
                  height: mm(b.h, scale),
                }}>
                  {cellZones}
                  {renderSlotOverlay?.(si)}
                </div>
              );
            }
            return cellZones;
          })}

        </div>
      )}
    </div>
  );
}
